import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useNavigation, NavigationProp } from '@react-navigation/native';

interface NavigationHistoryItem {
  routeName: string;
  params?: any;
  timestamp: number;
}

export const useMouseNavigation = () => {
  const navigation = useNavigation<any>();
  const isEnabled = useRef(true);
  const navigationHistory = useRef<NavigationHistoryItem[]>([]);
  const currentIndex = useRef(-1);



  useEffect(() => {
    // Only enable on web platform
    if (Platform.OS !== 'web') {
      return;
    }

    console.log('Setting up mouse navigation event listeners...');

    const handleMouseDown = (event: MouseEvent) => {
      if (!isEnabled.current) return;

      // Check if the event is from mouse buttons 4 (back) or 5 (forward)
      if (event.button === 3) {
        // Mouse button 4 (back)
        event.preventDefault();
        event.stopPropagation();
        if (navigation.canGoBack()) {
          navigation.goBack();
        } else {
          // Fallback to browser back
          try {
            if (window.history && window.history.back) {
              window.history.back();
            }
          } catch (error) {
            // Silent fail
          }
        }
      } else if (event.button === 4) {
        // Mouse button 5 (forward) - limited by browser history
        event.preventDefault();
        event.stopPropagation();
        // Try to use browser's forward functionality
        try {
          // Try to use browser's history.forward() if available
          if (window.history && window.history.forward) {
            window.history.forward();
          }
        } catch (error) {
          // Silent fail
        }
      }
    };

    // Additional handler for mouseup events (some browsers use this for side buttons)
    const handleMouseUp = (event: MouseEvent) => {
      if (!isEnabled.current) return;

      if (event.button === 3) {
        event.preventDefault();
        event.stopPropagation();
        if (navigation.canGoBack()) {
          navigation.goBack();
        } else {
          // Fallback to browser back
          try {
            if (window.history && window.history.back) {
              window.history.back();
            }
          } catch (error) {
            // Silent fail
          }
        }
      } else if (event.button === 4) {
        event.preventDefault();
        event.stopPropagation();
        try {
          if (window.history && window.history.forward) {
            window.history.forward();
          }
        } catch (error) {
          // Silent fail
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isEnabled.current) return;

      // Also support Alt+Left/Right for back/forward
      if (event.altKey) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          
          // Try React Navigation's goBack first
          if (navigation.canGoBack()) {
            navigation.goBack();
          } else {
            // Fallback to browser back
            try {
              if (window.history && window.history.back) {
                window.history.back();
              }
            } catch (error) {
              // Silent fail
            }
          }
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          // Try to use browser's forward functionality
          try {
            // Try to use browser's history.forward() if available
            if (window.history && window.history.forward) {
              window.history.forward();
            }
          } catch (error) {
            // Silent fail
          }
        }
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp); // Separate handler for mouseup
    document.addEventListener('click', handleMouseDown); // Fallback
    document.addEventListener('auxclick', handleMouseDown); // Middle mouse button and side buttons
    document.addEventListener('contextmenu', handleMouseDown); // Right click (might catch some side buttons)
    document.addEventListener('keydown', handleKeyDown);
    
    // Also try window level listeners (sometimes needed for side buttons)
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('click', handleMouseDown);
      document.removeEventListener('auxclick', handleMouseDown);
      document.removeEventListener('contextmenu', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [navigation]);

  // Function to temporarily disable mouse navigation (useful for text inputs, etc.)
  const disableMouseNavigation = () => {
    isEnabled.current = false;
  };

  // Function to re-enable mouse navigation
  const enableMouseNavigation = () => {
    isEnabled.current = true;
  };

  // Function to get current navigation state (for debugging)
  const getNavigationState = () => {
    return {
      canGoBack: navigation.canGoBack(),
      canGoForward: false, // Limited by browser history
    };
  };

  return {
    disableMouseNavigation,
    enableMouseNavigation,
    getNavigationState,
  };
};
